/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import React, { MouseEvent } from "react";
import { Avatar } from "@mui/material";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import AuthenticationClient from "../../../../Basis/Authentication/AuthenticationClient";
import { useAppKontoContext } from "../../../../Basis/Components/AppKontoContext";
import { IAppMandantContext, useAppMandantContext } from "../../../../Basis/Components/AppMandantContext";
import { useAppRollenContext } from "../../../../Basis/Components/AppRollenContext";
import IAppSnackbar from "../../../../Basis/Components/AppSnackbarContext/IAppSnackbar";
import { environment } from "../../../../Basis/environment";
import { ArrowRightToBracketIcon, BuildingIcon, CircleUserIcon, EMailIcon, KeyIcon, PlusIcon, UserIcon } from "../../../../Basis/FontAwesome";
import useQuery from "../../../../Basis/Utils/useQuery";
import ViewModel from "../../../../Basis/ViewModels/ViewModel";
import UIConstants from "../../../../UIConstants";
import LegeMandantZuKontoAn from "../../Commands/LegeMandantZuKontoAn";
import { translationKeys, useTranslation } from "../../Internationalisierung/i18nMandanten";
import LadeEigeneMandanten from "../../Queries/LadeEigeneMandanten";
import { Konto } from "../../Queries/LadeEigenesKonto";
import { Mandant } from "../../Queries/LadeOeffentlichenMandant";
import AendereEigeneEMailDialog from "../../Views/AendereEigeneEMailDialog";
import AendereEigenesPasswortDialog from "../../Views/AendereEigenesPasswortDialog";
import { useAuthenticationContext } from "./../../../../Basis/Authentication/AuthenticationContext";
import IAuthenticationContext from "./../../../../Basis/Authentication/AuthenticationContext/IAuthenticationContext";
import { useAppSnackbar } from "./../../../../Basis/Components/AppSnackbarContext";
import AppAccountHeaderItem from "./../AppAccountHeaderItem";
import "./AppAccount.scss";

export interface AppAccountState {
    anchorEl?: HTMLElement;
    isAendereEigenesPasswortDialogOpen: boolean;
    isAendereEMailDialogOpen: boolean;
}

export const createInitialState = (): AppAccountState => ({
    anchorEl: undefined,
    isAendereEigenesPasswortDialogOpen: false,
    isAendereEMailDialogOpen: false
});


export class AppAccountModel extends ViewModel<AppAccountState> {
    private readonly _snackbar: IAppSnackbar;
    private readonly _authContext: IAuthenticationContext | null;
    private readonly _konto: Konto | undefined;
    private readonly _appMandantContext: IAppMandantContext;
    private readonly _legeMandantZuKontoAn: LegeMandantZuKontoAn;
    private readonly _authenticationClient: AuthenticationClient;

    public constructor() {
        super({
            initialState: createInitialState(),
        });
        this._snackbar = useAppSnackbar();
        this._authContext = useAuthenticationContext();
        this._konto = useAppKontoContext().konto;
        this._appMandantContext = useAppMandantContext();
        this._legeMandantZuKontoAn = new LegeMandantZuKontoAn();
        this._authenticationClient = new AuthenticationClient();
    }

    public get konto(): Konto | undefined {
        return this._konto;
    }

    public get mandant(): Mandant | undefined {
        return this._appMandantContext.mandant;
    }

    public get isMenuOpen(): boolean {
        return Boolean(this.state.anchorEl);
    }

    public readonly abmelden = () => {
        try {
            this.closeMenu();
            window.location.href = UIConstants.logoutPath;
        } catch {
            // Nichts passiert
        }
    };

    public readonly eMailAendern = () => {
        this.setState((nextState) => {
            nextState.isAendereEMailDialogOpen = true;
        });
    }

    public readonly schliesseEMailAendernDialog = () => {
        this.setState((nextState) => {
            nextState.isAendereEMailDialogOpen = false;
        });
        this.closeMenu();
    }

    public readonly passwortAendern = () => {
        this.setState((nextState) => {
            nextState.isAendereEigenesPasswortDialogOpen = true;
        });
    }

    public readonly eigenesPasswortGeaendert = () => {
        this.setState((nextState) => {
            nextState.isAendereEigenesPasswortDialogOpen = false;
        });
        this.closeMenu();
    }

    public readonly aendereEigenesPasswortAbgebrochen = () => {
        this.setState((nextState) => {
            nextState.isAendereEigenesPasswortDialogOpen = false;
        });
    }

    public readonly openMenu = (e: MouseEvent<HTMLElement>) => {
        const currentTarget = e.currentTarget;
        this.setState((nextState) => {
            nextState.anchorEl = currentTarget;
        });
    };

    public readonly closeMenu = () => {
        this.setState((nextState) => {
            nextState.anchorEl = undefined;
        });
    };

    public readonly mandantAnlegen = (meldung: string) => {
        if (!this._konto) return;
        const eMail = this._konto.eMail;
        const zeitzone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const kultur = navigator.language;
        this.executeCommandAsync(async () => {
            this._appCoffeeMug.show(meldung);
            try {
                const angelegterMandant = await this._legeMandantZuKontoAn.execute({ eMail, zeitzone, kultur });
                if (angelegterMandant) {
                    const response = await this._authenticationClient.refreshToken();
                    if (response.ok) {
                        window.location.replace(`${environment.baseUrl}${angelegterMandant.kennung}`);
                    }
                }
            } finally {
                this._appCoffeeMug.hide();
            }
        });
    }
}

export default function AppAccount() {
    const vm = new AppAccountModel();
    const { t } = useTranslation();
    const { state } = useQuery({ query: LadeEigeneMandanten, input: {} });
    const appRollenContext = useAppRollenContext();

    return (
        <>
            <div className="lan-appaccount" onClick={vm.openMenu}>
                <div className="lan-appaccount__box">
                    <Typography className="lan-appaccount__konto" noWrap>
                        {vm.konto?.eMail}
                    </Typography>
                    <Typography className="lan-appaccount__mandant" noWrap>
                        {vm.mandant?.bezeichnung}
                    </Typography>
                </div>
                <CircleUserIcon className="lan-appaccount__icon" />
            </div>

            <Menu
                className="lan-appaccount-menu"
                anchorEl={vm.state.anchorEl}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                open={vm.isMenuOpen}
                onClose={vm.closeMenu}
                keepMounted
            >
                <div className="lan-appaccount__info">
                    <Avatar className="lan-appaccount__info__avatar"><UserIcon /></Avatar>
                    <div className="lan-appaccount__info__text">
                        <Typography className="lan-appaccount__konto" noWrap>
                            {vm.konto?.eMail}
                        </Typography>
                        <Typography className="lan-appaccount__mandant" noWrap>
                            {vm.mandant?.bezeichnung}
                        </Typography>
                    </div>
                </div>
                <MenuItem onClick={vm.abmelden}>
                    <ListItemIcon>
                        <ArrowRightToBracketIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={t(translationKeys.appAccount.abmelden)} />
                </MenuItem>
                <AppAccountHeaderItem text={t(translationKeys.appAccount.konto)} />
                <MenuItem onClick={vm.eMailAendern}>
                    <ListItemIcon>
                        <EMailIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={t(translationKeys.appAccount.eMailAendern)} />
                </MenuItem>
                <MenuItem onClick={vm.passwortAendern}>
                    <ListItemIcon>
                        <KeyIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={t(translationKeys.appAccount.passwortAendern)} />
                </MenuItem>
                <AppAccountHeaderItem text={t(translationKeys.appAccount.mandanten)} />
                { !state.loading
                    ? state.data?.mandanten.filter(mandant => mandant.istNutzend)
                        .map(mandant => (
                            <MenuItem
                                className="lan-appaccount__kontomandant"
                                key={mandant.mandantId}
                                component="a"
                                href={`${mandant.url}`}
                            >
                                <ListItemIcon>
                                    <BuildingIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primaryTypographyProps={{ noWrap: true }} primary={mandant.bezeichnung}/>
                            </MenuItem>))
                    : []
                }
                {(appRollenContext.istEigentuemerOderSupport) && (
                    <MenuItem onClick={() => vm.mandantAnlegen(t(translationKeys.appAccount.mandantAnlegenMeldung))}>
                        <ListItemIcon>
                            <PlusIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={t(translationKeys.appAccount.mandantAnlegen)} />
                    </MenuItem>
                )}
            </Menu>

            <AendereEigeneEMailDialog
                isOpen={vm.state.isAendereEMailDialogOpen}
                onEigeneEMailGeaendert={vm.schliesseEMailAendernDialog}
                onAendereEigeneEMailAbgebrochen={vm.schliesseEMailAendernDialog}
            />
            <AendereEigenesPasswortDialog
                isOpen={vm.state.isAendereEigenesPasswortDialogOpen}
                onEigenesPasswortGeaendert={vm.eigenesPasswortGeaendert}
                onAendereEigenesPasswortAbgebrochen={vm.aendereEigenesPasswortAbgebrochen}
            />
        </>
    );
}
